import 'ol/ol.css';
import Feature from 'ol/Feature';
import {LineString} from 'ol/geom';
import {
  Pointer as PointerInteraction,
} from 'ol/interaction';
import {Vector as VectorSource} from 'ol/source';
import {Vector as VectorLayer} from 'ol/layer';
import {Stroke,Style} from 'ol/style';

export default class LineDraw extends PointerInteraction {
    constructor(options) {
        super({
            handleDownEvent: handleDownEvent,
            handleDragEvent: handleDragEvent,
            handleUpEvent: handleUpEvent,
        });
        this.sketchFeature_=null; // 线段
        
        options=options||{};

        /**
         * Draw overlay where our sketch features are drawn.
         * @type {VectorLayer}
         * @private
         */
        this.overlay_ = new VectorLayer({
            source: new VectorSource({
                useSpatialIndex: false,
            }),
            style: options.style ? options.style : getDefaultStyleFunction(),
            updateWhileInteracting: true
        });
        
        /**
         * @type {boolean}
         * @private
         */
        this.shouldHandle_ = false;

        /**
         * @type {import("../pixel.js").Pixel}
         * @private
         */
        this.downPx_ = null;

        this.downCoor_=null; // 第一次按下时的坐标

        this.throttle_=null; // 节流函数
    }
    // 修改坐标位置
    modifyDrawing_(coordinate){
        const geometry = this.sketchFeature_.getGeometry();
        if(this.shouldHandle_){
            geometry.setCoordinates([this.downCoor_,coordinate]);
        }
    }
    /**
     * @private
     */
    updateState_() {
        const map = this.getMap();
        const active = this.getActive();
        if (!map || !active) {
            return;
        }
        this.overlay_.setMap(active ? map : null);
    }

}
/**
 * @return {import("../style/Style.js").StyleFunction} Styles.
 */
function getDefaultStyleFunction() {
    return new Style({
        stroke: new Stroke({
            color: '#ffcc33',
            width: 2,
        }),
        zIndex:1
    });
}
/**
 * @param {import("../src/ol/MapBrowserEvent.js").default} evt Map browser event.
 * @return {boolean} `true` to start the drag sequence.
 */
function handleDownEvent(evt) {
    this.updateState_();

    // 初始化绘图Feature
    this.sketchFeature_=new Feature({
        geometry:new LineString([]),
        _type:"line", // 类型名称
        _node_type:10, // 类型编号
    });
    // 设置id号，为什么要调用setId和set("id"),这是因为如果设置了set("id")，那么getId的时候就找不到id号
    let fid=(new Date()).getTime();
    this.sketchFeature_.setId(fid);
    
    // 记录点击值
    this.shouldHandle_=false;
    this.downPx_=evt.pixel;
    this.downCoor_=evt.coordinate;

    // 判断当前节点是否有可吸附对象，如果有则将按下点吸附到相关的位置
    const map = this.getMap();
    
    let temp=calculateSnapCoors(this.sketchFeature_,evt.pixel,"node1",map);
    if(temp.downCoor_){
        this.downCoor_=temp.downCoor_;
    }
    if(temp.downPx_){
        this.downPx_=temp.downPx_;
    }
    
    // 加到地图上
    this.overlay_.getSource().addFeature(this.sketchFeature_); 

    return true;
}
function handleDragEvent(evt) {
    if (this.downPx_&&!this.shouldHandle_) {
        const downPx = this.downPx_;
        const clickPx = evt.pixel;
        const dx = downPx[0] - clickPx[0];
        const dy = downPx[1] - clickPx[1];
        const squaredDistance = dx * dx + dy * dy;
        this.shouldHandle_ = squaredDistance > 36;
        if (!this.shouldHandle_) {
            return;
        }
    }
  
    // 进行线段的绘制
    if(this.shouldHandle_&&this.downPx_){
        let coor_=evt.coordinate;
        // // 进行坐标的修改
        this.modifyDrawing_(coor_);

        // 当超出临界值的时候，进行触发下一个吸附
        let xpixel=evt.pixel[0];
        let ypixel=evt.pixel[1];
        let hitTolerance=15;
        if(Math.abs(xpixel-this.downPx_[0])>hitTolerance&&Math.abs(ypixel-this.downPx_[1])>hitTolerance){
            let map=this.getMap();
            let temp=calculateSnapCoors(this.sketchFeature_,evt.pixel,"node2",map);
            if(temp.downCoor_){
                coor_=temp.downCoor_;
            }
            // 进行坐标的修改
            this.modifyDrawing_(coor_);
        }
    }
    return true;
}

/**
 * @return {boolean} `false` to stop the drag sequence.
 */
function handleUpEvent(evt) {
    if(this.shouldHandle_){
        // 判断是否可以有停靠的点
        let map=this.getMap();
        let temp=calculateSnapCoors(this.sketchFeature_,evt.pixel,"node2",map);
        let coor_=evt.coordinate;
        if(temp.downCoor_){
            coor_=temp.downCoor_;
        }

        // 进行坐标修改
        this.modifyDrawing_(coor_);
        // 触发绘图完成事件
        this.dispatchEvent({type:"drawend",event:evt,feature:this.sketchFeature_});

        // 删除图层
        this.overlay_.getSource().clear();
    }

    this.shouldHandle_=false;
    this.downPx_=null;
    this.downCoor_=null;
    this.sketchFeature_=null;
    this.throttle_=null;

    return false;
}

/** 计算停靠点 */
function calculateSnapCoors(sketchFeature_,pixel,endpoint,map){
    let downPx_=null;
    let downCoor_=null;

    let hitTolerance=15; // 吸附半径
    let snapFeature=map.forEachFeatureAtPixel(pixel, function (feature) {
        if(feature!=sketchFeature_){ // 找到第一个不属于自身的
            return feature;
        }
    },{
        hitTolerance:hitTolerance
    });
    if(snapFeature){
        let geometry_=snapFeature.getGeometry();
        if(geometry_ instanceof LineString){ // 如果吸附位置是一条直线
            let xpixel=pixel[0];
            let ypixel=pixel[1];
            // 判断是否靠近第一个点
            let first_coor=geometry_.getFirstCoordinate();
            let first_pixel=map.getPixelFromCoordinate(first_coor);
            if(Math.abs(xpixel-first_pixel[0])<=hitTolerance&&Math.abs(ypixel-first_pixel[1])<=hitTolerance){
                downPx_=first_pixel;
                downCoor_=first_coor;
            }
            // 判断是否靠近最后一个点
            let last_coor=geometry_.getLastCoordinate();
            let last_pixel=map.getPixelFromCoordinate(last_coor);
            if(Math.abs(xpixel-last_pixel[0])<=hitTolerance&&Math.abs(ypixel-last_pixel[1])<=hitTolerance){
                downPx_=last_pixel;
                downCoor_=last_coor;
            }

        }else{ // 如果吸附位置不是直线，是节点
            downCoor_=geometry_.getCoordinates();
            downPx_=map.getPixelFromCoordinate(downCoor_);

            // 线段绑定节点,因为绘制的是线段的开始节点，所以就绑定到了线段的头节点上
            endpoint=endpoint||"node1";
            sketchFeature_.set(endpoint,snapFeature.getId()); 

            // 根据不同的节点类型，进行节点绑定线段
            // 点绑定线段
            let nodeProps=snapFeature.getProperties();
            let lines=snapFeature.get("lines");
            if(!lines){
                lines=[];
            }
            if((lines.length<=1&&nodeProps._node_type==1)||nodeProps._node_type==0){ // 如果节点类型为1，只能绑定一条线
                lines.push(sketchFeature_.getId());
            }
            // 点绑定线
            snapFeature.set("lines",lines);
        }
    }

    return {
        downPx_:downPx_,
        downCoor_:downCoor_
    }
}